import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import UsersService from "@velocity-ehs/user-utils-module";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import GatewayService from "../services/GatewayService";
import {
  CreateLocationInput,
  LocationFormInput,
  UpdateLocationInput,
} from "../components/routeComponents/Locations/Location";
import fieldTrim from "../components/supportFunctions/FieldTrim";

export enum LocationActions {
  CREATE = "create",
  UPDATE = "update",
  INACTIVATE = "inactivate",
  ARCHIVE = "archive",
}

const useLocationMutation = () => {
  const create = useMutation(
    (req: CreateLocationInput) => GatewayService.createLocation(req),
    ResponseHandling("location_created_successfully")
  );
  const update = useMutation(
    (req: UpdateLocationInput) => GatewayService.updateLocation(req),
    ResponseHandling("location_update_success")
  );
  const inactivateLocation = useMutation(
    (req: UpdateLocationInput) => GatewayService.updateLocation(req),
    ResponseHandling("inactivate_location_success_message")
  );

  const archiveLocation = useMutation(
    (req: UpdateLocationInput) => GatewayService.updateLocation(req),
    ResponseHandling("archive_location_success_message")
  );

  const mutate = async (
    action: LocationActions,
    input: LocationFormInput,
    locationId: string
  ) => {
    switch (action) {
      case LocationActions.CREATE:
        const createLocation = mapFormToCreate(input, locationId);
        return await create.mutateAsync(createLocation);

      case LocationActions.UPDATE:
        const updateLocation = mapFormToUpdate(input, locationId);
        return await update.mutateAsync(updateLocation);

      case LocationActions.INACTIVATE:
        const inactivate = mapFormToUpdate(input, locationId);
        return await inactivateLocation.mutateAsync(inactivate);

      case LocationActions.ARCHIVE:
        const archive = mapFormToUpdate(input, locationId);
        return await archiveLocation.mutateAsync(archive);

      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: create.isLoading || update.isLoading,
    },
  };
};

export default useLocationMutation;

function trimFormValues(formValues: LocationFormInput) {
  return {
    ...formValues,
    name: formValues.name?.trim(),
    address: formValues.isPhysicalLocation
      ? fieldTrim({ formData: formValues.address })
      : null,
  };
}

function mapFormToCreate(
  input: LocationFormInput,
  parentId: string
): CreateLocationInput {
  const trimmedInput = trimFormValues(input);

  return {
    customerID: UsersService.customer?.id,
    name: trimmedInput.name,
    address1: trimmedInput?.address?.address1,
    address2: trimmedInput?.address?.address2,
    city: trimmedInput?.address?.city,
    country: trimmedInput?.address?.nation?.countryCode,
    ein: trimmedInput?.ein,
    establishmentType: trimmedInput?.establishmentType?.id,
    industryDescription: trimmedInput?.industryDescription,
    locationStatus: trimmedInput?.locationStatus,
    naicsCode: trimmedInput?.naicsCode,
    postalCode: trimmedInput?.address?.postalCode,
    shortCode: trimmedInput?.shortCode,
    sicCode: trimmedInput?.sicCode,
    stateProvince: trimmedInput?.address?.subdivision?.name,
    parentID: parentId,
  };
}

function mapFormToUpdate(
  input: LocationFormInput,
  locationId: string
): UpdateLocationInput {
  const trimmedInput = trimFormValues(input);

  return {
    id: locationId,
    name: trimmedInput.name,
    address1: input.isPhysicalLocation ? trimmedInput?.address?.address1 : "",
    address2: input.isPhysicalLocation ? trimmedInput?.address?.address2 : "",
    city: input.isPhysicalLocation ? trimmedInput?.address?.city : "",
    country: input.isPhysicalLocation
      ? trimmedInput?.address?.nation?.countryCode
      : null,
    ein: trimmedInput?.ein,
    establishmentType: trimmedInput?.establishmentType?.id,
    industryDescription: trimmedInput?.industryDescription,
    locationStatus: trimmedInput?.locationStatus,
    naicsCode: trimmedInput?.naicsCode,
    postalCode: input.isPhysicalLocation
      ? trimmedInput?.address?.postalCode
      : "",
    shortCode: trimmedInput?.shortCode,
    sicCode: trimmedInput?.sicCode,
    stateProvince: input.isPhysicalLocation
      ? trimmedInput?.address?.subdivision?.name
      : null,
  };
}

function ResponseHandling(messageKey: string) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
  };
}
